import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const AuditReportPage = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const [logsRes, usersRes, filesRes, eventsRes] = await Promise.all([
      supabase.from('logs').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('profiles').select('*'),
      supabase.from('files').select('*'),
      supabase.from('suspicious_events').select('*').order('created_at', { ascending: false }),
    ]);
    setLogs(logsRes.data || []);
    setUsers(usersRes.data || []);
    setFiles(filesRes.data || []);
    setEvents(eventsRes.data || []);
    setLoading(false);
  };

  const exportReport = () => {
    const now = new Date().toISOString();
    const totalStorage = files.reduce((s, f) => s + (f.file_size || 0), 0);
    const formatBytes = (b) => { if (!b) return '0 B'; const k = 1024; const s = ['B','KB','MB','GB']; const i = Math.floor(Math.log(b)/Math.log(k)); return Math.round(b/Math.pow(k,i)*100)/100+' '+s[i]; };

    let report = `SECUREVAULT - SECURITY AUDIT REPORT\n`;
    report += `Generated: ${now}\n`;
    report += `${'='.repeat(60)}\n\n`;
    report += `SUMMARY\n${'-'.repeat(40)}\n`;
    report += `Total Users: ${users.length}\n`;
    report += `Total Files: ${files.length}\n`;
    report += `Total Storage: ${formatBytes(totalStorage)}\n`;
    report += `Encrypted Files: ${files.filter(f => f.encryption_iv).length}\n`;
    report += `Unencrypted Files: ${files.filter(f => !f.encryption_iv).length}\n`;
    report += `Suspicious Events: ${events.length}\n`;
    report += `Unresolved Alerts: ${events.filter(e => !e.resolved).length}\n\n`;

    report += `USERS\n${'-'.repeat(40)}\n`;
    users.forEach(u => { report += `${u.name || 'N/A'} | ${u.email} | ${u.role} | Joined: ${new Date(u.created_at).toLocaleDateString()}\n`; });

    report += `\nSUSPICIOUS EVENTS\n${'-'.repeat(40)}\n`;
    if (events.length === 0) report += `No suspicious events detected.\n`;
    events.forEach(e => { report += `[${e.severity?.toUpperCase()}] ${e.event_type} - ${e.description} | ${new Date(e.created_at).toLocaleString()} | ${e.resolved ? 'RESOLVED' : 'OPEN'}\n`; });

    report += `\nRECENT ACTIVITY LOG (Last 50)\n${'-'.repeat(40)}\n`;
    logs.slice(0, 50).forEach(l => { report += `${new Date(l.created_at).toLocaleString()} | ${l.action} | IP: ${l.ip_address || 'N/A'}\n`; });

    report += `\n${'='.repeat(60)}\nEnd of Report\n`;

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `securevault_audit_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit report downloaded!');
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="audit-title">
              Security Audit Report
            </h1>
            <p className="text-base text-muted-foreground mt-1">System-wide security overview and exportable report</p>
          </div>
          <Button onClick={exportReport} disabled={loading} data-testid="export-report-btn">
            <Download className="w-4 h-4 mr-2" /> Export Report
          </Button>
        </div>

        {loading ? <p className="text-muted-foreground font-mono text-center py-12">Loading...</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-6">
              <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{users.length}</p>
              <p className="text-sm text-muted-foreground">Total Users</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{files.length}</p>
              <p className="text-sm text-muted-foreground">Total Files</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{files.filter(f => f.encryption_iv).length}</p>
              <p className="text-sm text-muted-foreground">Encrypted Files</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <p className={`text-2xl font-semibold ${events.filter(e=>!e.resolved).length > 0 ? 'text-destructive' : 'text-[hsl(var(--success))]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {events.filter(e => !e.resolved).length}
              </p>
              <p className="text-sm text-muted-foreground">Unresolved Alerts</p>
            </div>
          </div>
        )}

        {events.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Suspicious Events</h2>
            <div className="space-y-2">
              {events.slice(0, 10).map(e => (
                <div key={e.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <span className={`text-xs font-bold uppercase mr-2 ${e.severity === 'critical' ? 'text-destructive' : e.severity === 'high' ? 'text-orange-500' : 'text-[hsl(var(--warning))]'}`}>{e.severity}</span>
                    <span className="text-sm">{e.event_type}</span>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AuditReportPage;
